import { Button } from '../../../../../../widgets/buttons';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';
import Stack from '../../../../../../widgets/stack/stack';

interface ComposerMergeAttributeOutput {
    newAttribute: ComposerAttributeNode;
    attributesToMerge?: ComposerAttributeNode[];
    deleteSourceAttributes: boolean;
}
interface ComposerMergeAttributeDialogProps {
    attributesList: ComposerAttributeNode[];
    onConfirm: (attributes: ComposerMergeAttributeOutput) => void;
}
const ComposerMergeAttributeWindow = styled(Dialog)`
    max-width: 600px;
`;
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;
const FormInline = styled.div`
display: flex
;
    flex-direction: row;
    align-items: center;
	min-width: 300px;
    gap: 15px;
}
`;

const FormBlock = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
    max-height: 300px;
    overflow: auto;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
`;

const ComposerMergeAttributeDialogContent: React.FC<ComposerMergeAttributeDialogProps> = ({ attributesList, onConfirm }) => {
    const validationContext = useValidationContext();
    const [deleteSourceAttributeState, setDeleteSourceAttributeState] = React.useState<boolean>(false);
    const [attributesToMerge, setAttributesToMerge] = React.useState<ComposerAttributeNode[]>([]);
    const [attributeName, setAttributeName] = React.useState<string>('');
    const [attributeCode, setAttributeCode] = React.useState<string>('');

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const newParsedAttribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        newParsedAttribute.name = attributeName;
        newParsedAttribute.code = attributeCode;
        const result: ComposerMergeAttributeOutput = {
            newAttribute: newParsedAttribute,
            attributesToMerge,
            deleteSourceAttributes: deleteSourceAttributeState
        };
        onConfirm(result);
    };

    const onAttributeToMerge = (attribute: ComposerAttributeNode) => {
        const exists = attributesToMerge.find((a) => a.id === attribute.id);
        if (exists) {
            setAttributesToMerge(attributesToMerge.filter((a) => a.id !== attribute.id));
        } else {
            setAttributesToMerge([...attributesToMerge, attribute]);
        }
    };

    return (
        <Dialog
            title={T._('Select Attributes to Merge', 'Admin')}
            windowDecorator={ComposerMergeAttributeWindow}
            buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}
        >
            <Container>
                <FormInline>
                    <p>{T._('Please select below the attributes you want to merge into a single attribute.', 'Admin')}</p>
                    <Button
                        onClick={() => {
                            if (attributesList.length === attributesToMerge.length) {
                                setAttributesToMerge([]);
                            } else {
                                attributesList.forEach((attr) => {
                                    if (!attributesToMerge.includes(attr)) {
                                        setAttributesToMerge((prev) => [...prev, attr]);
                                    }
                                });
                            }
                        }}
                    >
                        {attributesList.length === attributesToMerge.length ? T._('Deselect all attributes', 'Admin') : T._('Select all attributes', 'Admin')}
                    </Button>
                </FormInline>
                <Stack>
                    <FormControl label={T._('Attributes to merge', 'Admin')}>
                        <ValidationBox
                            fullWidth
                            validationKey='composerMergeAttributes'
                            validationLabel={T._('Attributes to merge', 'Admin')}
                            validationValue={attributesToMerge}
                            validate={(value) => value.length > 0}
                            validationMessage={T._('At least one attribute to merge is required.', 'Admin')}
                        >
                            <FormInline>
                                {attributesList.map((attr) => (
                                    <FormBlock key={attr.id} onClick={() => onAttributeToMerge(attr)}>
                                        <Checkbox label={attr.name} checked={attributesToMerge.includes(attr)} onChange={() => {}} />
                                    </FormBlock>
                                ))}
                            </FormInline>
                        </ValidationBox>
                    </FormControl>
                    <FormControl label={T._('New Attribute Name', 'Admin')}>
                        <Input
                            type='text'
                            value={attributeName}
                            onChange={(e) => setAttributeName(e.target.value)}
                            validationKey='composerMergeAttributeName'
                            validationLabel={T._('New Attribute Name', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('New Attribute Name is required.', 'Admin')}
                        />
                    </FormControl>
                    <FormControl label={T._('New Attribute Code', 'Admin')}>
                        <Input
                            type='text'
                            value={attributeCode}
                            onChange={(e) => setAttributeCode(e.target.value)}
                            validationKey='composerMergeAttributeCode'
                            validationLabel={T._('New Attribute Code', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('New Attribute Code is required.', 'Admin')}
                        />
                    </FormControl>
                </Stack>
                <FormInline>
                    <Checkbox
                        label={T._('Delete source attributes', 'Admin')}
                        checked={deleteSourceAttributeState}
                        onChange={(e) => {
                            setDeleteSourceAttributeState(e);
                        }}
                    />
                </FormInline>
            </Container>
        </Dialog>
    );
};

export const ComposerMergeAttributeDialog: React.FC<ComposerMergeAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerMergeAttributeDialogContent {...props} />
    </ValidationStateProvider>
);
